import { DsmButton, DsmGrid, DsmIcon } from "@dsm-dcs/design-system-react";
import { Dispatch, FC, SetStateAction } from "react";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { CattleFeed, FreshGrass } from "../../../../models/Intervention/CattleIntervention";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { ArrayField, useFormContext } from "react-hook-form";
import { DairyIntervention } from "../../../../models/Intervention";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import RowTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import { v4 as uuidv4 } from "uuid";
import * as BaselineDairyV2FreshGrassComponent from "../../Baseline/DairyV2/DairyV2FeedFreshGrassComponent";
import { FormType } from "../../common";
import { isFieldFilled } from "../CommonDataParts/InterventionGenericInfoDialog";
import { Spacer } from "../../../helpers/Spacer";
import EditModeButtons from "../../../helpers/EditModeButtons";
import { Tooltip } from "@material-ui/core";

type FreshGrassInput = {
  id: string;
  type: string;
  amount: string;
  crudeProteinContent: string;
  dryMatterContent: string;
  digestibility: string;
  neutralDetergentFiber: string;
  grossEnergyContent: string;
  ashContent: string;
}

interface DairyV2FeedFreshGrassComponentProp {
  formType: FormType;
  fieldItemPrefix: string;
  currentStageData: CattleFeed;
  isContinuousGrowth: boolean;
  isFreshGrassEdit: boolean;
  setIsFreshGrassEdit: Dispatch<SetStateAction<boolean>>;
  freshGrass: Partial<ArrayField<Record<string, any>, "keyId">>[];
  appendFreshGrass: (value: Partial<Record<string, any>> | Partial<Record<string, any>>[], shouldFocus?: boolean) => void;
  removeFreshGrass: (index?: number | number[]) => void;
  resetEdit: () => void;
  resetFreshGrassAdditionToDefaultValues: () => void;
}

const DairyV2FeedFreshGrassComponent: FC<DairyV2FeedFreshGrassComponentProp> = ({
  formType,
  fieldItemPrefix,
  currentStageData,
  isContinuousGrowth,
  isFreshGrassEdit,
  setIsFreshGrassEdit,
  freshGrass,
  appendFreshGrass,
  removeFreshGrass,
  resetEdit,
  resetFreshGrassAdditionToDefaultValues,
}) => {
  const intl = useIntl();
  const classes: CSSClassesList = processAndStageStylesV2();
  const fc = useFormContext<DairyIntervention>();
  const barnOutputMassUnit = "kg DM";

  const addFreshGrass = () => {
    const newFreshGrass: FreshGrassInput = {
      id: uuidv4(),
      type: "",
      amount: "",
      crudeProteinContent: "",
      dryMatterContent: "",
      digestibility: "",
      neutralDetergentFiber: "",
      grossEnergyContent: "",
      ashContent: "",
    };
    appendFreshGrass(newFreshGrass);
  };

  const getCorrectBorderDisplay = () => {
    if ((currentStageData.freshGrass?.length ?? 0) > 1 || freshGrass.length)
      return classes.dsmGridTwoColumnNoRowSpacingWithBorder;

    return classes.dsmGridTwoColumnNoRowSpacing;
  };

  const shouldDisplayResetButton = isFreshGrassEdit && freshGrass.length > 0;

  const freshGrassUnit = (fg: FreshGrass, index: number) => {
    const fieldPrefix = `${fieldItemPrefix}.freshGrass[${index}]`;

    return (
      <DsmGrid className={getCorrectBorderDisplay()}>
        <Spacer />
        <Spacer />
        {!isFreshGrassEdit ? (
          <>
            <ReactHookDsmInput
              name={`${fieldPrefix}.type`}
              label={intl.formatMessage({
                id: 'SUSTELL.STAGE.DAIRY.FEED.FRESH_GRASS_NAME',
              })}
              tooltip={intl.formatMessage({
                id: !isContinuousGrowth
                  ? 'SUSTELL.STAGE.DAIRY.FEED.FRESH_GRASS_NAME.TOOLTIP'
                  : 'SUSTELL.STAGE.DAIRY.FEED.CONTINUOUS_GROWING.FRESH_GRASS_NAME.TOOLTIP',
              })}
              adornment={intl.formatMessage({
                id: 'SUSTELL.STAGE.DAIRY.FEED.FRESH_GRASS_NAME.ADORNMENT',
              })}
              type="text"
              defaultValue={fg.type ?? ''}
              disabled
            />
            {isFieldFilled(fc.getValues(`${fieldPrefix}.amount`)) ? (
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldPrefix}.amount`}
                  label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.AMOUNT' })}
                  tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.AMOUNT.TOOLTIP' })}
                  metricUnit={barnOutputMassUnit}
                  baseline={fg.amount}
                  displayTypeSelect={false}
                  type="number"
                  disabled
                />
                <Spacer />
              </div>
            ) : (
              <ReactHookDsmInput
                name={`${fieldPrefix}.originalAmount`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.AMOUNT' })}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.AMOUNT.TOOLTIP' })}
                defaultValue={fg.amount ?? ''}
                adornment={barnOutputMassUnit}
                type="number"
                disabled
              />
            )}
            {isFieldFilled(fc.getValues(`${fieldPrefix}.crudeProteinContent`)) ? (
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldPrefix}.crudeProteinContent`}
                  label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.CRUDE_PROTEIN_CONTENT' })}
                  tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.CRUDE_PROTEIN_CONTENT.TOOLTIP' })}
                  metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.CRUDE_PROTEIN_CONTENT.ADORNMENT' })}
                  baseline={fg.crudeProteinContent}
                  displayTypeSelect={false}
                  type="number"
                  disabled
                />
                <Spacer />
              </div>
            ) : (
              <ReactHookDsmInput
                name={`${fieldPrefix}.originalCrudeProteinContent`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.CRUDE_PROTEIN_CONTENT' })}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.CRUDE_PROTEIN_CONTENT.TOOLTIP' })}
                adornment={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.CRUDE_PROTEIN_CONTENT.ADORNMENT' })}
                placeholder={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.CRUDE_PROTEIN_CONTENT.PLACEHOLDER' })}
                defaultValue={fg.crudeProteinContent ?? ''}
                type="number"
                disabled
              />
            )}
            {isFieldFilled(fc.getValues(`${fieldPrefix}.dryMatterContent`)) ? (
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldPrefix}.dryMatterContent`}
                  label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.DRY_MATTER_CONTENT' })}
                  tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.DRY_MATTER_CONTENT.TOOLTIP' })}
                  metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.DRY_MATTER_CONTENT.ADORNMENT' })}
                  baseline={fg.dryMatterContent}
                  displayTypeSelect={false}
                  type="number"
                  disabled
                />
                <Spacer />
              </div>
            ) : (
              <ReactHookDsmInput
                name={`${fieldPrefix}.originalDryMatterContent`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.DRY_MATTER_CONTENT' })}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.DRY_MATTER_CONTENT.TOOLTIP' })}
                adornment={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.DRY_MATTER_CONTENT.ADORNMENT' })}
                placeholder={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.DRY_MATTER_CONTENT.PLACEHOLDER' })}
                defaultValue={fg.dryMatterContent ?? ''}
                type="number"
                disabled
              />
            )}
            {isFieldFilled(fc.getValues(`${fieldPrefix}.digestibility`)) ? (
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldPrefix}.digestibility`}
                  label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.DIGESTIBILITY' })}
                  tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.DIGESTIBILITY.TOOLTIP' })}
                  metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.DIGESTIBILITY.ADORNMENT' })}
                  baseline={fg.digestibility}
                  displayTypeSelect={false}
                  type="number"
                  disabled
                />
                <Spacer />
              </div>
            ) : (
              <ReactHookDsmInput
                name={`${fieldPrefix}.originalDigestibility`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.DIGESTIBILITY' })}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.DIGESTIBILITY.TOOLTIP' })}
                adornment={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.DIGESTIBILITY.ADORNMENT' })}
                placeholder={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.DIGESTIBILITY.PLACEHOLDER' })}
                defaultValue={fg.digestibility ?? ''}
                type="number"
                disabled
              />
            )}
            {!isContinuousGrowth &&
              (isFieldFilled(fc.getValues(`${fieldPrefix}.neutralDetergentFiber`)) ? (
                <div>
                  <RowTextFieldWithMetrics
                    name={`${fieldPrefix}.neutralDetergentFiber`}
                    label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.NEUTRAL_DETERGENT_FIBER' })}
                    tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.NEUTRAL_DETERGENT_FIBER.TOOLTIP' })}
                    metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.NEUTRAL_DETERGENT_FIBER.ADORNMENT' })}
                    baseline={fg.neutralDetergentFiber}
                    displayTypeSelect={false}
                    type="number"
                    disabled
                  />
                  <Spacer />
                </div>
              ) : (
                <ReactHookDsmInput
                  name={`${fieldPrefix}.originalNeutralDetergentFiber`}
                  label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.NEUTRAL_DETERGENT_FIBER' })}
                  tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.NEUTRAL_DETERGENT_FIBER.TOOLTIP' })}
                  adornment={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.NEUTRAL_DETERGENT_FIBER.ADORNMENT' })}
                  placeholder={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.NEUTRAL_DETERGENT_FIBER.PLACEHOLDER' })}
                  defaultValue={fg.neutralDetergentFiber ?? ''}
                  type="number"
                  disabled
                />
              ))}
            {isFieldFilled(fc.getValues(`${fieldPrefix}.grossEnergyContent`)) ? (
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldPrefix}.grossEnergyContent`}
                  label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.GROSS_ENERGY_CONTENT' })}
                  tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.GROSS_ENERGY_CONTENT.TOOLTIP' })}
                  metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.GROSS_ENERGY_CONTENT.ADORNMENT' })}
                  baseline={fg.grossEnergyContent}
                  displayTypeSelect={false}
                  type="number"
                  disabled
                />
                <Spacer />
              </div>
            ) : (
              <ReactHookDsmInput
                name={`${fieldPrefix}.originalGrossEnergyContent`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.GROSS_ENERGY_CONTENT' })}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.GROSS_ENERGY_CONTENT.TOOLTIP' })}
                adornment={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.GROSS_ENERGY_CONTENT.ADORNMENT' })}
                placeholder={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.GROSS_ENERGY_CONTENT.PLACEHOLDER' })}
                defaultValue={fg.grossEnergyContent ?? ''}
                type="number"
                disabled
              />
            )}
            {isFieldFilled(fc.getValues(`${fieldPrefix}.ashContent`)) ? (
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldPrefix}.ashContent`}
                  label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.ASH_CONTENT' })}
                  tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.ASH_CONTENT.TOOLTIP' })}
                  metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.ASH_CONTENT.ADORNMENT' })}
                  baseline={fg.ashContent}
                  displayTypeSelect={false}
                  type="number"
                  disabled
                />
                <Spacer />
              </div>
            ) : (
              <ReactHookDsmInput
                name={`${fieldPrefix}.originalAshContent`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.ASH_CONTENT' })}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.ASH_CONTENT.TOOLTIP' })}
                adornment={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.ASH_CONTENT.ADORNMENT' })}
                placeholder={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.ASH_CONTENT.PLACEHOLDER' })}
                defaultValue={fg.ashContent ?? ''}
                type="number"
                disabled
              />
            )}
          </>
        ) : (
          <>
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldPrefix}.amount`}
                label={fg.type as string}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.AMOUNT.TOOLTIP' })}
                metricUnit={barnOutputMassUnit}
                baseline={fg.amount}
                disabled={false}
                type="number"
              />
              <Spacer />
            </div>
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldPrefix}.crudeProteinContent`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.CRUDE_PROTEIN_CONTENT' })}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.CRUDE_PROTEIN_CONTENT.TOOLTIP' })}
                metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.CRUDE_PROTEIN_CONTENT.ADORNMENT' })}
                baseline={fg.crudeProteinContent}
                disabled={false}
                type="number"
              />
              <Spacer />
            </div>
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldPrefix}.dryMatterContent`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.DRY_MATTER_CONTENT' })}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.DRY_MATTER_CONTENT.TOOLTIP' })}
                metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.DRY_MATTER_CONTENT.ADORNMENT' })}
                baseline={fg.dryMatterContent}
                disabled={false}
                type="number"
              />
              <Spacer />
            </div>
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldPrefix}.digestibility`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.DIGESTIBILITY' })}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.DIGESTIBILITY.TOOLTIP' })}
                metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.DIGESTIBILITY.ADORNMENT' })}
                baseline={fg.digestibility}
                disabled={false}
                type="number"
              />
              <Spacer />
            </div>
            {!isContinuousGrowth &&
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldPrefix}.neutralDetergentFiber`}
                  label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.NEUTRAL_DETERGENT_FIBER' })}
                  tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.NEUTRAL_DETERGENT_FIBER.TOOLTIP' })}
                  metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.NEUTRAL_DETERGENT_FIBER.ADORNMENT' })}
                  baseline={fg.neutralDetergentFiber}
                  disabled={false}
                  type="number"
                />
                <Spacer />
              </div>}
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldPrefix}.grossEnergyContent`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.GROSS_ENERGY_CONTENT' })}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.GROSS_ENERGY_CONTENT.TOOLTIP' })}
                metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.GROSS_ENERGY_CONTENT.ADORNMENT' })}
                baseline={fg.grossEnergyContent}
                disabled={false}
                type="number"
              />
              <Spacer />
            </div>
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldPrefix}.ashContent`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.ASH_CONTENT' })}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.ASH_CONTENT.TOOLTIP' })}
                metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.ASH_CONTENT.ADORNMENT' })}
                baseline={fg.ashContent}
                disabled={false}
                type="number"
              />
              <Spacer />
            </div>
          </>
        )}
      </DsmGrid>
    )
  }

  return (
    <DsmGrid className={classes.dsmGridOneColumnWithBorder}>
      {(currentStageData?.freshGrass || [])
        ?.filter((item) => !!item.type)
        ?.map((freshGrassItem, index: number) => (freshGrassUnit(freshGrassItem, index)))}
      {!!freshGrass.length && freshGrass.map((item, index) => (
        <BaselineDairyV2FreshGrassComponent.default
          key={item.keyId}
          fieldItemPrefix={`${fieldItemPrefix}.freshGrassAdditions`}
          formType={formType}
          itemIndex={index}
          removeHandler={(index) => removeFreshGrass(index)}
          isDairyCowStage={!isContinuousGrowth}
          isIntervention
          isInterventionEdit={isFreshGrassEdit}
        />))}
      {formType !== FormType.View && (
        <>
          {isFreshGrassEdit && (
            <>
              {freshGrass?.length === 0 && <Spacer />}
              <DsmButton
                variant="text"
                onClick={addFreshGrass}
              >
                <DsmIcon slot="before" name="general/plus-circle" />
                {intl.formatMessage({ id: 'GENERAL.ADD.ANOTHER' })}{' '}
                {intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.GRASS' })}
              </DsmButton>
            </>
          )}
          {shouldDisplayResetButton && (
            <div className={classes.resetButtonContainer}>
              <DsmButton
                widthFull
                variant="text"
                onClick={() => resetFreshGrassAdditionToDefaultValues()}
                className="mt-3"
              >
                <DsmIcon slot="before" name="arrows/refresh-cw-01" />
                <u style={{ textDecoration: 'none' }}>
                  {intl.formatMessage({
                    id: 'SUSTELL.STAGE.DAIRY.RESET_DEFAULT_VALUES',
                  })}
                </u>
              </DsmButton>
              <Tooltip
                title={intl.formatMessage({
                  id: 'SUSTELL.RESET_DEFAULT.TOOLTIP',
                })}
                placement="left"
                className='ml-3'
                style={{ transform: 'translateY(85%)' }}
              >
                <DsmIcon
                  name="general/help-circle"
                  className={classes.inlineTooltipClass}
                  title=""
                />
              </Tooltip>
            </div>
          )}
          <EditModeButtons
            isEditing={isFreshGrassEdit}
            onEdit={() => setIsFreshGrassEdit(true)}
            onCancel={resetEdit}
          />
        </>)}
    </DsmGrid>
  )
}

export default DairyV2FeedFreshGrassComponent;
